import { BehaviorSubject, take } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HomeApiClientService, NIASBizAPIDtosAuthResponseDto, NIASBizAPIDtosUserDataDto, NIASBizBllModelsAuthorizeVm, SessionApiClientService } from '../api/v1';
import { menuIsActAsModelRights, menuIsCanOpenGivenList, menuIsCanOpenRecivedList, menuIsLegalRights, menuIsPersonRights } from '../constants/menu-list-access-rights.constant';
import { Environment } from '../constants/enviroment.constant';
import { Params, Router } from '@angular/router';
import { EAuthorizationEServicesCreateRightsRoute, EAuthorizationHomeRoute, EAuthorizationManagmentAssignRightsRoute, ErrorRoute, NiasAuthRoute } from '../constants/routes.constant';
import { AlertService } from '../components/alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private homeApiClientService: HomeApiClientService = inject(HomeApiClientService);
  private sessionApiClientService: SessionApiClientService = inject(SessionApiClientService);
  private router: Router = inject(Router);
  private alertService: AlertService = inject(AlertService);

  private user: BehaviorSubject<NIASBizAPIDtosUserDataDto | null> = new BehaviorSubject<NIASBizAPIDtosUserDataDto | null>(null);
  private token: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private actAsModelProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    const data: string | null = window.sessionStorage.getItem('user');
    const user: NIASBizAPIDtosUserDataDto | null = JSON.parse(data ? data : "{}");
    if(user) {
      this.saveUser(user);
      this.saveToken(user.NavToken as string);
    }
  }

  private saveToken(token: string): void {
    this.token.next(token);
  }

  private saveUser(user: NIASBizAPIDtosUserDataDto) {
    window.sessionStorage.setItem('user', JSON.stringify(user));
    this.saveToken(user.NavToken as string);
    this.user.next(user);
  }

  public get userValue(): NIASBizAPIDtosUserDataDto | null {
    return this.user.getValue();
  }

  public get tokenValue(): string | null {
    return this.token.getValue();
  }

  public get isActAsModelProgress(): boolean {
    return this.user.getValue()?.ActAsModel || this.actAsModelProgress.getValue() ? false : true;
  }

  public isAuthenticate(): boolean {
    return this.tokenValue ? true : false;
  }

  public loginNias(): void {
    this.homeApiClientService
      .homeApiLoginNias(Environment.regular.niasRedirectUrl, Environment.regular.niasRedirectErrorUrl, true)
      .pipe(take(1))
      .subscribe((url: string) => {
        window.location.href = url;
      });
  }

  public reAuthNias(redirectUrl: string, level: number): void {
    this.homeApiClientService
      .homeApiLoginNiasReauth(redirectUrl, Environment.regular.niasRedirectErrorUrl, level, true)
      .pipe(take(1))
      .subscribe((url: string) => {
        window.location.href = url;
      });
  }

  public getUser(redirectUrl?: string | null): void {
    this.sessionApiClientService.sessionApiGet().subscribe({
      next: (user: NIASBizAPIDtosUserDataDto | null) => {
        if(!user) {
          this.loginNias();
        }else if(user && user.NavToken) {
          this.saveUser(user);
          this.saveToken(user.NavToken);
          this.router.navigateByUrl(redirectUrl ? redirectUrl : EAuthorizationHomeRoute);
          this.alertService.open('success', 'Uspješna prijava u sustav');
        }else {
          this.router.navigateByUrl(ErrorRoute)
        }
      },
      error: (error: unknown) => {
        this.router.navigateByUrl(ErrorRoute);
      },
    });
  }

  public actAsModel(params: Params): void {
    this.actAsModelProgress.next(true);
    this.homeApiClientService.homeApiActAs(params).subscribe({
      next: (user: NIASBizAPIDtosAuthResponseDto | null) => {
        if (!user) {
          this.router.navigateByUrl(NiasAuthRoute);
        } else {
          this.saveUser(user.User_data!);
          this.saveToken(user.User_data?.NavToken!);
          window.location.href = `${window.location.protocol}//${window.location.host}/${EAuthorizationHomeRoute}`;
          this.alertService.open('success', 'Uspješna promjena subjekta');
        }
      },
      error: () => {
        this.router.navigateByUrl(NiasAuthRoute);
        this.actAsModelProgress.next(false);
      },
    });
  }

  public checkSecurityLevel(level: number): boolean {
    if (!this.userValue?.SecurityLevel) {
      return false;
    } else {
      if (this.userValue.SecurityLevel >= level) {
        return true;
      } else {
        return false;
      }
    }
  }

  public accessMenuRights(id: string): boolean {
    const actAsModel: boolean = this.user.getValue()?.ActAsModel ? true : false;
    const isCompany: boolean = this.user.getValue()?.CompanyName ? true : false;
    const isCanOpenReceivedList: boolean = this.user.getValue()?.UserDataExtended?.CanOpenReceivedAuthDocsPage ? true : false;
    const isCanOpenGivenList: boolean = this.user.getValue()?.UserDataExtended?.CanOpenGivenAuthDocsPage ? true : false;

    let result = true;

    if(menuIsActAsModelRights.includes(id)) {
      result = actAsModel;
    }

    if(menuIsLegalRights.includes(id)) {
      result = isCompany && result;
    }

    if(menuIsPersonRights.includes(id)) {
      result = !isCompany && result;
    }

    if(id == EAuthorizationManagmentAssignRightsRoute) {
      const isCanOpenCreateAuthorization: boolean = this.user.getValue()?.UserDataExtended?.CanOpenCreateRoAuthDocPage ? true : false;
      result = isCanOpenCreateAuthorization && result;
    }

    if(id == EAuthorizationEServicesCreateRightsRoute) {
      const isCanOpenCreateService: boolean = this.user.getValue()?.UserDataExtended?.CanOpenCreateServiceAuthDocPage ? true : false; 
      result = isCanOpenCreateService && result;
    }

    if(menuIsCanOpenRecivedList.includes(id)) {
      result = isCanOpenReceivedList && result;
    }

    if(menuIsCanOpenGivenList.includes(id)) {
      result = isCanOpenGivenList && result;
    }

    return result;
  }

  public logout() {
    this.user.next(null);
    this.token.next(null);
    window.sessionStorage.removeItem('user');
  }

  public getBaseAuthDocumentRequest(): NIASBizBllModelsAuthorizeVm {
    const authorizeModel: NIASBizBllModelsAuthorizeVm = {};
    
    authorizeModel.IdService = 1;   
    authorizeModel.FromPersonEntityOib = this.userValue?.UserDataExtended?.LoggedPersonOib;
    authorizeModel.FromPersonEntityFirstName = this.userValue?.UserDataExtended?.LoggedPersonFirstName;
    authorizeModel.FromPersonEntityLastName = this.userValue?.UserDataExtended?.LoggedPersonLastName;

    if(this.userValue?.UserDataExtended?.LoggedLegalId) {
      authorizeModel.FromLegalEntityIzvor_reg = this.userValue?.UserDataExtended?.LoggedLegalIzvor_reg;
      authorizeModel.FromLegalEntityIps = this.userValue?.UserDataExtended?.LoggedLegalIps;
      authorizeModel.FromLegalEntityName = this.userValue?.UserDataExtended?.LoggedLegalName;
    }

    if(this.userValue?.ActAsModel?.ForPersonOib){
      authorizeModel.ForPersonEntityOIB = this.userValue.ActAsModel.ForPersonOib;
      authorizeModel.ForPersonEntityFirstName = this.userValue.ActAsModel.ForPersonFirstName;
      authorizeModel.ForPersonEntityLastName = this.userValue.ActAsModel.ForPersonLastName;
    }else if(this.userValue?.ActAsModel?.ForLegalIps) {
      authorizeModel.ForLegalEntityIps = this.userValue.ActAsModel?.ForLegalIps;
      authorizeModel.ForLegalEntityIzvor_reg = this.userValue.ActAsModel.ForLegalIzvor_reg;
      authorizeModel.ForLegalEntityName = this.userValue.ActAsModel.ForLegalName;
    }else {
      authorizeModel.ForPersonEntityOIB = this.userValue?.UserDataExtended?.LoggedPersonOib
    }
    return authorizeModel;
  }

}
